import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setAppError, setBreadCrumb } from "../../../../store/userSlice";
import { Link, useLocation } from "react-router-dom";
import Addlocaltransfer from "./addLocaltransfer";
import LocaltransferPopUp from "./localtransferpopup";
import SmartDownloadPagination from "../../../../components/SmartDataTable/SmartDownloadPagination";
import nodeinstance from "../../../../nodeInstance";

// Column definitions matching the original headers
const OPS1_COLUMNS = [
  { key: "serial",          title: "S.NO.",            searchable: false },
  { key: "point_of_hire",   title: "POINT OF HIRE",    searchable: true  },
  { key: "candidate_id",    title: "EMPLOYEE ID",      searchable: true  },
  { key: "passport_number", title: "PASSPORT NUMBER",  searchable: true  },
  { key: "quotation_type",  title: "QUOTATION TYPE",   searchable: true  },
  { key: "project_Name",    title: "PROJECT NAME",     searchable: true  },
  { key: "nationality",     title: "NATIONALITY",      searchable: true  },
  { key: "religion",        title: "RELIGION",         searchable: true  },
  { key: "actual_profession", title: "ACTUAL PROFESSION", searchable: true },
  { key: "sponsor_id",      title: "SPONSOR ID",       searchable: true  },
  { key: "Sector_Name",     title: "ARRIVAL SECTOR",   searchable: true  },
  { key: "visa_profession", title: "VISA PROFESSION",  searchable: true  },
  { key: "arrival_date",    title: "DATE OF ARRIVAL",  searchable: true  },
  { key: "status",          title: "CANDIDATE STATUS", searchable: true  },
];

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export default function Ops1masterDatabase() {
  const dispatch = useDispatch();
  const token = localStorage.getItem("nodeToken");
  const location = useLocation();

  const [route, setRoute] = useState(false);
  const [trigger, setTrigger] = useState(null);
  const [uploadPopup, setUploadPopup] = useState(false);

  useEffect(() => {
    setRoute(location.pathname.split("/")[3] === "create-local-transfer");
  }, [location]);

  useEffect(() => {
    dispatch(setBreadCrumb([{ label: "OPS 1 MASTER DATABASE", link: "" }]));
  }, [location]);

  // Custom cell renderer
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case "serial":
        return (currentPage - 1) * pageSize + rowIndex + 1;

      case "sponsor_id":
        // OVERSEAS candidates use VRF sponsor id
        return item.point_of_hire === "OVERSEAS"
          ? (item.VRF_Sponsor_id || "-")
          : (item.sponsor_id || "-");

      case "arrival_date":
        return formatDate(item.arrival_date);

      default:
        return null;
    }
  }, []);

  const [templateLoading, setTemplateLoading] = useState(false);

  const downloadTemplate = useCallback(async () => {
    setTemplateLoading(true);
    try {
      const response = await nodeinstance.get(
        "/dashboard/localTransfer/downloadExcel-template",
        { responseType: "blob" }
      );
      if (response.status === 200) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "LOCAL_TRANSFER_TEMPLATE.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        dispatch(setAppError({ msg: "Template Downloaded", color: "success" }));
      }
    } catch (err) {
      dispatch(setAppError({ msg: err.message || "Template download failed" }));
    } finally {
      setTemplateLoading(false);
    }
  }, [dispatch]);

  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({ msg: `Excel Downloaded (${result.recordCount} records)`, color: "success" }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({ msg: error.error || "Download failed" }));
  }, [dispatch]);

  // Additional action buttons rendered in the toolbar
  const additionalActions = useMemo(() => [
    {
      label: templateLoading ? "Downloading..." : "Download Template",
      onClick: downloadTemplate,
      disabled: templateLoading,
      className: "p-2 px-4 rounded-md bg-blue text-white min-w-fit text-sm disabled:opacity-60 disabled:cursor-not-allowed",
    },
    {
      label: "Upload Local Transfer",
      onClick: () => setUploadPopup(true),
      className: "p-2 px-4 rounded-md bg-blue text-white min-w-fit text-sm",
    },
  ], [downloadTemplate, templateLoading]);


  if (route) {
    return <Addlocaltransfer setTrigger={setTrigger} trigger={trigger} />;
  }

  return (
    <div className="pt-2 uppercase m-3">
      {/* Top bar: Add Local Transfer button */}
      <div className="mb-3">
        <Link to="/dashboard/Ops-1-Master-Database/create-local-transfer">
          <button className="p-2 bg-red text-white rounded-md" style={{ width: "200px" }}>
            ADD LOCAL TRANSFER
          </button>
        </Link>
      </div>

      <SmartDownloadPagination
        // API — Node backend, uses nodeToken (injected by NodeInstance interceptor)
        apiEndpoint="/dashboard/localTransfer"
        downloadEndpoint="/dashboard/localTransfer/downloadexcel_testing"
        token={token}

        // Table
        columns={OPS1_COLUMNS}
        enableHighlighting={true}

        // Pagination
        defaultPageSize={50}
        pageSizeOptions={[10, 25, 50, 100, 500]}

        // Download
        downloadFilename="OPS1_Master_Database"
        enableDownload={true}

        // Search
        enableSearch={true}
        searchPlaceholder="Search across all columns..."
        searchDebounceMs={400}

        // Cache invalidation after upload/create
        trigger={trigger}

        // Custom rendering
        renderCustomCell={renderCustomCell}

        // Toolbar buttons
        additionalActions={additionalActions}

        // Event handlers
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}

        noDataMessage="No data found."
      />

      {uploadPopup && (
        <LocaltransferPopUp
          PopUp={uploadPopup}
          setPopUp={setUploadPopup}
          setTrigger={setTrigger}
        />
      )}
    </div>
  );
}
